/** Shared affine triangle clip helpers for raster-warped SVG export. */

export function fmtSvgNum(n: number): string {
    return parseFloat(n.toFixed(3)).toString();
}

export function escapeSvgAttr(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

/**
 * Solve the 2x3 affine [a c e; b d f] mapping (u_i, v_i) -> (x_i, y_i)
 * for i = 0, 1, 2. Returns `null` if the source triangle is degenerate.
 */
export function solveAffine(
    u0: number,
    v0: number,
    x0: number,
    y0: number,
    u1: number,
    v1: number,
    x1: number,
    y1: number,
    u2: number,
    v2: number,
    x2: number,
    y2: number,
): [number, number, number, number, number, number] | null {
    const du1 = u1 - u0;
    const dv1 = v1 - v0;
    const du2 = u2 - u0;
    const dv2 = v2 - v0;
    const det = du1 * dv2 - du2 * dv1;
    if (Math.abs(det) < 1e-12) return null;
    const inv = 1 / det;

    const dx1 = x1 - x0;
    const dx2 = x2 - x0;
    const dy1 = y1 - y0;
    const dy2 = y2 - y0;

    const a = (dx1 * dv2 - dx2 * dv1) * inv;
    const c = (du1 * dx2 - du2 * dx1) * inv;
    const b = (dy1 * dv2 - dy2 * dv1) * inv;
    const d = (du1 * dy2 - du2 * dy1) * inv;
    const e = x0 - a * u0 - c * v0;
    const f = y0 - b * u0 - d * v0;
    return [a, b, c, d, e, f];
}

/** Scale triangle vertices outward from centroid to reduce AA seams between clips. */
export function expandTriangle(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    factor: number,
): [number, number, number, number, number, number] {
    const cx = (x0 + x1 + x2) / 3;
    const cy = (y0 + y1 + y2) / 3;
    return [
        cx + (x0 - cx) * factor,
        cy + (y0 - cy) * factor,
        cx + (x1 - cx) * factor,
        cy + (y1 - cy) * factor,
        cx + (x2 - cx) * factor,
        cy + (y2 - cy) * factor,
    ];
}

export const CLIP_OVERSCAN = 1.01;

export interface AffineImageTriangleSlice {
    /** Source UV triangle (u0,v0, u1,v1, u2,v2). */
    src: [number, number, number, number, number, number];
    /** Destination XY triangle (x0,y0, x1,y1, x2,y2). */
    dst: [number, number, number, number, number, number];
}

/**
 * Emit defs + body fragments for one affine-mapped clipped `<use>` of `imageId`.
 */
export function appendAffineClippedImageUse(
    defs: string[],
    body: string[],
    imageId: string,
    slice: AffineImageTriangleSlice,
    clipIdPrefix: string,
    clipIdxRef: { value: number },
    overscan = CLIP_OVERSCAN,
): void {
    const t = [...slice.src, ...slice.dst] as [
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
    ];
    // `t` is [u0,v0, u1,v1, u2,v2, x0,y0, x1,y1, x2,y2] (all src, then all dst),
    // but solveAffine takes the coords interleaved per vertex
    // (u,v,x,y) — so pair each source UV with its destination XY.
    const m = solveAffine(
        t[0],
        t[1],
        t[6],
        t[7],
        t[2],
        t[3],
        t[8],
        t[9],
        t[4],
        t[5],
        t[10],
        t[11],
    );
    if (!m) return;
    const [a, b, c, d, e, f] = m;
    const clipId = `${clipIdPrefix}${clipIdxRef.value++}`;
    const [ex0, ey0, ex1, ey1, ex2, ey2] = expandTriangle(
        t[6],
        t[7],
        t[8],
        t[9],
        t[10],
        t[11],
        overscan,
    );
    const points =
        `${fmtSvgNum(ex0)},${fmtSvgNum(ey0)} ` +
        `${fmtSvgNum(ex1)},${fmtSvgNum(ey1)} ` +
        `${fmtSvgNum(ex2)},${fmtSvgNum(ey2)}`;
    defs.push(
        `<clipPath id="${clipId}" clipPathUnits="userSpaceOnUse"><polygon points="${points}" /></clipPath>`,
    );
    body.push(
        `<g clip-path="url(#${clipId})">` +
            `<use href="#${imageId}" xlink:href="#${imageId}" ` +
            `transform="matrix(${fmtSvgNum(a)} ${fmtSvgNum(b)} ${fmtSvgNum(c)} ${fmtSvgNum(d)} ${fmtSvgNum(e)} ${fmtSvgNum(f)})" />` +
            `</g>`,
    );
}
